import {
  StapleTableHeader,
  StapleTableCell,
  StapleTableRow,
  StapleTableHeading,
  StapleTableBody,
  useFirstRender,
} from '@insurancemenu/imenu-components-lib';
import {
  EmployeeInvitationStatusTypes,
  SubscriberProgress,
  SubscribersSortBy,
  SubscribersSortOrder,
} from 'types/electionService';
import SubscriberStatus from 'components/common/election/SubscriberStatus';
import { SendInvitationModal } from 'components/common/add-employees/SendInvitationModal';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { ActionsUiStores, EmployeeActionUiPresenter } from 'store/modules/add-employees/employeeDelegate';
import { AvailableActions } from 'components/common/add-employees/AvailableActions';
import WarningModals from 'components/common/add-employees/WarningModals';
import { DependentsQuantity } from 'components/common/add-employees/DependentsQuantity';
import { AddEmployeesFilters } from 'store/pages/enrollment/add-employees/pageStore';
import SubscriberProgressTag from 'components/common/add-employees/SubscriberProgressTag';
import IAddEmployeesPage from 'store/modules/add-employees/iAddEmployeesPage';
import { InviteEmployeeAction } from 'store/modules/add-employees/types';
import { EnrollmentEmployeeAction } from 'store/pages/enrollment/add-employees/enrollmentEmployeeActions';
import {
  Mail,
  UserCheck,
  SupervisorAccount,
  Person,
  Notification,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { ActionCellStyled, StapleTableStyled } from './styles';
import { ADD_EMPLOYEES_TABLE_TEST_IDS } from '../constants';

interface EmployeeEntry {
  name: string;
  progress: SubscriberProgress;
  selfEnrollStatus: EmployeeInvitationStatusTypes | null;
  employeeClass: string;
  dependentsNumber: number;
  actions: EmployeeActionUiPresenter[];
  actionsUiStores: ActionsUiStores;
}

export const enrollmentFlowEmployeeActionIcons: Partial<Record<string, React.FC>> = {
  [InviteEmployeeAction.SEND_INVITATION]: Mail,
  [InviteEmployeeAction.RESEND_INVITATION]: Mail,
  [EnrollmentEmployeeAction.EMPLOYEE_PROFILE]: Person,
  [EnrollmentEmployeeAction.MANAGE_DEPENDENTS]: SupervisorAccount,
  [EnrollmentEmployeeAction.ELECTIONS]: UserCheck,
  [EnrollmentEmployeeAction.REMIND_COMPLETE_PROFILE]: Notification,
};

const AddEmployeesTableRow: React.FC<EmployeeEntry> = observer(
  ({ name, progress, selfEnrollStatus, employeeClass, dependentsNumber, actions, actionsUiStores }) => {
    return (
      <StapleTableRow data-test-id={ADD_EMPLOYEES_TABLE_TEST_IDS.TABLE_ROW}>
        <StapleTableCell width="6rem">
          {name}
          <DependentsQuantity dependentsNumber={dependentsNumber} />
        </StapleTableCell>
        <StapleTableCell width="6rem">
          {selfEnrollStatus && <SubscriberStatus status={selfEnrollStatus} />}
        </StapleTableCell>
        <StapleTableCell width="6rem">
          <SubscriberProgressTag status={progress} />
        </StapleTableCell>
        <StapleTableCell width="6rem">{employeeClass}</StapleTableCell>
        <ActionCellStyled width="5rem">
          <AvailableActions
            availableActions={actions}
            warningModals={actionsUiStores.warningModalDelegates}
            actionIcons={enrollmentFlowEmployeeActionIcons}
          />
          {actionsUiStores.sendInvitationModalStore && (
            <SendInvitationModal modalStore={actionsUiStores.sendInvitationModalStore} />
          )}
          <WarningModals availableActions={actions} warningModals={actionsUiStores.warningModalDelegates} />
        </ActionCellStyled>
      </StapleTableRow>
    );
  },
);

interface Props {
  pageStore: IAddEmployeesPage<AddEmployeesFilters>;
  isStepCompleted?: boolean;
}

export const AddEmployeesTable: React.FC<Props> = observer(({ pageStore, isStepCompleted = false }) => {
  const { getEmployees, paginationDelegate, employees, isLoading } = pageStore;

  const onEmployeeNameSort = (sortKey: string, sortingOrder: 'ASC' | 'DESC'): void => {
    paginationDelegate.setSortBy(sortKey as SubscribersSortBy);
    paginationDelegate.setSortOrder(sortingOrder as SubscribersSortOrder);
  };

  const isFirstRender = useFirstRender();

  useEffect(() => {
    void getEmployees();
  }, [paginationDelegate.page]);

  useEffect(() => {
    if (isFirstRender) return;

    if (paginationDelegate.page !== 1) {
      paginationDelegate.setPage(1);
      paginationDelegate.setTotalPages(1);
      return;
    }

    void getEmployees();
  }, [
    paginationDelegate.sortBy,
    paginationDelegate.sortOrder,
    paginationDelegate.filters.employeeName,
    paginationDelegate.filters.subProgress,
    paginationDelegate.filters.status,
    paginationDelegate.filters.employeeClass,
  ]);

  return (
    <StapleTableStyled
      data-test-id={ADD_EMPLOYEES_TABLE_TEST_IDS.TABLE}
      totalElements={paginationDelegate.totalElements}
      loading={isLoading}
      pagination={{
        tablePage: paginationDelegate.page,
        minPage: 1,
        maxPage: paginationDelegate.totalPages,
        onPageChange: (page) => {
          paginationDelegate.setPage(page);
        },
      }}
    >
      <StapleTableHeader>
        <StapleTableRow>
          <StapleTableHeading
            width="6rem"
            sorting
            sortingKey={SubscribersSortBy.NAME}
            onSortCallback={onEmployeeNameSort}
          >
            Employee Name
          </StapleTableHeading>
          <StapleTableHeading width="6rem">Status</StapleTableHeading>
          <StapleTableHeading width="6rem">Progress</StapleTableHeading>
          <StapleTableHeading width="6rem">Employee Class</StapleTableHeading>
          <StapleTableHeading width="5rem" />
        </StapleTableRow>
      </StapleTableHeader>
      <StapleTableBody>
        {employees.map(({ rawData, fullName, dependentsNumber, availableActions, actionsUiStores }) => (
          <AddEmployeesTableRow
            key={rawData.id}
            name={fullName}
            progress={rawData.progress}
            selfEnrollStatus={rawData.selfEnrollStatus}
            employeeClass={rawData.employeeClass}
            dependentsNumber={dependentsNumber}
            actions={isStepCompleted ? [] : availableActions}
            actionsUiStores={actionsUiStores}
          />
        ))}
      </StapleTableBody>
    </StapleTableStyled>
  );
});
